import { useEffect, useState } from "react";
import { buildPriorityOptions } from "../components/visuals/PriorityIndicator";
import {
  EngagementDataSource,
  EngagementKind,
  EngagementRecord,
  EngagementSubthemeMetric,
  EngagementType,
  Momentum,
} from "../models/stewardshipModels";
import { TaskPriority } from "../models/workflowModels";
import { ENGAGEMENT_META_THEME_IMAGES } from "../pages/CompanyEngagements/constants";
import {
  buildBadgeStatusOptions,
  findSubThemeMetric,
} from "../pages/CompanyEngagements/utils";
import { Filter } from "../types/PageFilterTypes";
import { usePost } from "./usePost";
import { useToast } from "../store/ToastProvider";
import { MESSAGES } from "../messages";
import { AvatarProps } from "../components/visuals/Avatar";
import { formatReadable, isNotNan } from "../utils/formattingUtils";

type SelectableParams = {
  updatedPriority?: TaskPriority;
  updatedMomentum?: Momentum;
};

export const useEngagementSubThemeMetrics = (
  companyId: string
): UseEngagementSubThemeMetricProps => {
  const { addToast } = useToast();
  const {
    error,
    sendData: upsertEngagementSubThemeMetric,
    data: upsertResult,
  } = usePost<void, EngagementSubthemeMetric[]>();
  const [dataSource, setDataSource] = useState<EngagementDataSource[]>([]);
  const [selectableParams, setSelectableParams] = useState<
    Record<string, SelectableParams>
  >({});

  const updateSelectableParams = (
    theme: string,
    field: "updatedPriority" | "updatedMomentum",
    value: TaskPriority | Momentum
  ): void => {
    setSelectableParams((prev) => ({
      ...prev,
      [theme]: {
        ...(prev[theme] || {}),
        [field]: value,
      },
    }));
  };

  useEffect(() => {
    if (Object.keys(selectableParams).length) {
      const params: EngagementSubthemeMetric[] = Object.entries(
        selectableParams
      )
        .map(([key, value]) => {
          const metric = dataSource.find((item) => item.key === key);
          if (metric) {
            return buildUpsertMetricParams(metric, value);
          }
          return null;
        })
        .filter(Boolean) as EngagementSubthemeMetric[];

      upsertEngagementSubThemeMetric(
        "UPSERT_ENGAGEMENT_SUBTHEME_METRICS",
        params
      );
    }
  }, [selectableParams]);

  useEffect(() => {
    if (upsertResult) {
      addToast(
        MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ENGAGEMENT_SUBTHEME_METRICS
          .UPDATE_SUCCESS,
        "success"
      );
    }
    if (error) {
      addToast(
        error.error_user_message ||
          MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ENGAGEMENT_SUBTHEME_METRICS
            .UPDATE_ERROR,
        "error"
      );
    }
  }, [upsertResult, error]);

  const buildDataSource = (
    engagementRecords: EngagementRecord[],
    engagementSubThemeMetrics: EngagementSubthemeMetric[]
  ): void => {
    type Grouped = Record<
      string,
      {
        corporateIssues: number;
        corporateObjectives: number;
        metaTheme: EngagementType;
        managers: AvatarProps[];
      }
    >;

    const grouped = engagementRecords.reduce<Grouped>((acc, record) => {
      const theme = record.sub_theme;
      const manager = record.manager_name;
      const image = isNotNan(record.image) ? record.image : null;
      if (!theme) return acc;

      if (!acc[theme]) {
        acc[theme] = {
          corporateIssues: 0,
          corporateObjectives: 0,
          metaTheme: record.meta_theme,
          managers: manager
            ? [{ username: manager, image, tooltip: true }]
            : [],
        };
      } else {
        if (!acc[theme].metaTheme && record.meta_theme) {
          acc[theme].metaTheme = record.meta_theme;
        }
        if (
          manager &&
          !acc[theme].managers.some((m) => m.username === manager)
        ) {
          acc[theme].managers.push({ username: manager, image, tooltip: true });
        }
      }

      if (record.engagement_type === EngagementKind.CorporateObjective) {
        acc[theme].corporateObjectives += 1;
      } else {
        acc[theme].corporateIssues += 1;
      }

      return acc;
    }, {});

    setDataSource(
      Object.entries(grouped)
        .sort(([, a], [, b]) => {
          if (!a.metaTheme) return 1;
          if (!b.metaTheme) return -1;
          return b.metaTheme.localeCompare(a.metaTheme);
        })
        .map(([theme, counts]) => ({
          key: theme,
          theme: {
            username: counts.metaTheme || theme,
            displayText: formatReadable(theme),
            image: ENGAGEMENT_META_THEME_IMAGES[counts.metaTheme]?.logo,
          },
          corporateIssues: counts.corporateIssues,
          corporateObjectives: counts.corporateObjectives,
          managers: counts.managers,
          priority: {
            options: buildPriorityOptions(
              findSubThemeMetric(engagementSubThemeMetrics, theme)?.priority,
              theme
            ),
            onSelectOption: (option: Filter) =>
              updateSelectableParams(
                theme,
                "updatedPriority",
                option.key as TaskPriority
              ),
            isInTable: true,
          },
          momentum: {
            options: buildBadgeStatusOptions(
              findSubThemeMetric(engagementSubThemeMetrics, theme)?.momentum,
              theme
            ),
            onSelectOption: (option: Filter) =>
              updateSelectableParams(
                theme,
                "updatedMomentum",
                option.key as Momentum
              ),
            isInTable: true,
          },
        }))
    );
  };

  const buildUpsertMetricParams = (
    metric: EngagementDataSource,
    selectableParams: SelectableParams
  ): EngagementSubthemeMetric => {
    const initialMomentum = metric.momentum.options[0].key as Momentum;
    const initialPriority = metric.priority.options[0].key as TaskPriority;

    return {
      engagement_subtheme_metric_id: `${companyId}-${metric.key}`,
      compass_cid: companyId,
      sub_theme: metric.key,
      priority: selectableParams.updatedPriority || initialPriority,
      momentum: selectableParams.updatedMomentum || initialMomentum,
    };
  };

  return {
    dataSource,
    buildDataSource,
  };
};

interface UseEngagementSubThemeMetricProps {
  dataSource: EngagementDataSource[];
  buildDataSource: (
    engagementRecords: EngagementRecord[],
    engagementSubThemeMetrics: EngagementSubthemeMetric[]
  ) => void;
}
